import React, { ReactNode, useState } from 'react';
import styled, { css } from 'styled-components';

import MaterialSymbol from '#/components/basic/icons/MaterialSymbol';
import SearchInput from '#/components/basic/inputs/SearchInput';
import RouterLink from '#/components/basic/Link/RouterLink';
import { useTextCallbackThrottle } from '#/components/hooks';

import HeaderChainLogo from './HeaderChainLogo';

const Wrapper = styled.div<{ $marginBottom?: boolean }>`
  height: calc(
    ${({ $marginBottom }) =>
      $marginBottom
        ? 'var(--mobileNavHeight) - 8px'
        : 'var(--mobileNavHeight) - 4px'}
  );
  min-height: 50px;
  padding-right: 5px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  color: var(--white);
  font-family: var(--standardFontFamily);
  font-size: var(--medium2);

  background: var(--elevation-light);
  border-radius: 2px;
  margin: 0 4px
    ${({ $marginBottom: marginBottom }) => (marginBottom ? '4px' : '0')};
`;

const ContentWrap = styled.div`
  display: flex;
  align-items: center;
`;

const clickableCss = css`
  display: inline-block;
  margin-right: 10px;
  cursor: pointer;
`;

const BackLink = styled(RouterLink)`
  ${clickableCss}
  padding-left: 16px;
`;

const BackClickable = styled.div`
  ${clickableCss}
  padding-left: 16px;
`;

const SearchIconStyled = styled(MaterialSymbol)`
  opacity: 0.8;
  cursor: pointer;
  border-bottom: 1px solid transparent;
`;

const RightIcons = styled.div`
  display: flex;
  align-items: center;
  padding-left: 8px;
  padding-right: 11px;
`;

const CloseIconWrap = styled.div`
  display: inline-block;
  padding-left: 24px;
`;

function SearchComponentContainer({
  onSearch,
}: {
  onSearch: Props['onSearch'];
}) {
  const [searchOpened, setSearchOpened] = useState(false);
  const [searchInstantText, setSearchInstantText] = useState('');

  // Throttle calling parent callback for slick fast text typing experience
  useTextCallbackThrottle(searchInstantText, onSearch, 300);

  if (!searchOpened) {
    return (
      <SearchIconStyled icon="search" onClick={() => setSearchOpened(true)} />
    );
  }

  return (
    <SearchInput
      value={searchInstantText}
      onBlur={() => {
        if (!searchInstantText && searchOpened) {
          setSearchOpened(false);
        }
      }}
      onChange={setSearchInstantText}
      appearAnimation
    />
  );
}

type Props = {
  children?: ReactNode;
  onSearch?: (text: string) => void;
  chainLogo?: boolean;
  backGoTo?: string;
  onBackClick?: () => void;
  /** Optional Close icon on the right side of menu (For modal like screens) */
  onCloseClick?: () => void;
  rightContent?: ReactNode;
  marginBottom?: boolean;
};

function HeaderMobile({
  backGoTo,
  chainLogo,
  onBackClick,
  onCloseClick,
  onSearch,
  children,
  rightContent,
  marginBottom,
}: Props) {
  // const isDashboard = useIsDashboardRoute();
  const backVisible = backGoTo || onBackClick;
  return (
    <Wrapper $marginBottom={marginBottom}>
      <ContentWrap>
        {onBackClick && (
          <BackClickable onClick={onBackClick} color="white">
            <MaterialSymbol icon="arrow_back" />
          </BackClickable>
        )}
        {backGoTo && (
          <BackLink to={backGoTo} color="white">
            <MaterialSymbol icon="arrow_back" />
          </BackLink>
        )}
        {chainLogo ? (
          <HeaderChainLogo />
        ) : !backVisible ? (
          <div style={{ paddingLeft: 10 }} />
        ) : null}
        {children}
      </ContentWrap>
      <RightIcons>
        {onSearch && <SearchComponentContainer onSearch={onSearch} />}
        {rightContent}
        {onCloseClick && (
          <CloseIconWrap>
            <MaterialSymbol icon="close" onClick={onCloseClick} />
          </CloseIconWrap>
        )}
      </RightIcons>
    </Wrapper>
  );
}

export default HeaderMobile;
